// In-game settings: reticle/inrange overlays and ambient/battle music toggles.
// Persisted in localStorage; synced to the server when playing with a cloud account.

import { fetchSettings, saveSettings, type UserSettings } from '../net/api';
import { onMusicSettingChanged } from '../audio/index';
import { ensureTitleScreenStyles, getTitleShell } from '../ui/title-screen';

const LS_RETICLE = 'meritous_show_reticle';
const LS_INRANGE = 'meritous_show_inrange';
const LS_BGM_AMBIENT = 'meritous_bgm_ambient';
const LS_BGM_BATTLE = 'meritous_bgm_battle';

let syncMode: 'cloud' | 'offline' = 'offline';
let values: UserSettings = defaultValues();

function defaultValues(): UserSettings {
  return {
    reticle: true,
    inrange: true,
    bgm_ambient: true,
    bgm_battle: true,
  };
}

function readBool(key: string, fallback: boolean): boolean {
  const v = localStorage.getItem(key);
  if (v === null) return fallback;
  return v === '1';
}

function writeBool(key: string, v: boolean): void {
  localStorage.setItem(key, v ? '1' : '0');
}

function migrateLegacyBgmMute(): void {
  if (localStorage.getItem('meritous_bgm_mute') !== '1') return;
  if (localStorage.getItem(LS_BGM_AMBIENT) === null) {
    localStorage.setItem(LS_BGM_AMBIENT, '0');
  }
  if (localStorage.getItem(LS_BGM_BATTLE) === null) {
    localStorage.setItem(LS_BGM_BATTLE, '0');
  }
}

function loadFromLocalStorage(): UserSettings {
  migrateLegacyBgmMute();
  return {
    reticle: readBool(LS_RETICLE, true),
    inrange: readBool(LS_INRANGE, true),
    bgm_ambient: readBool(LS_BGM_AMBIENT, true),
    bgm_battle: readBool(LS_BGM_BATTLE, true),
  };
}

function persistLocal(): void {
  writeBool(LS_RETICLE, values.reticle);
  writeBool(LS_INRANGE, values.inrange);
  writeBool(LS_BGM_AMBIENT, values.bgm_ambient);
  writeBool(LS_BGM_BATTLE, values.bgm_battle);
}

function persistCloud(): void {
  if (syncMode !== 'cloud') return;
  void saveSettings({ ...values }).catch(() => undefined);
}

function applyValues(next: UserSettings): void {
  values = { ...next };
  persistLocal();
  onMusicSettingChanged();
}

export async function initSettings(mode: 'cloud' | 'offline'): Promise<void> {
  syncMode = mode;
  if (mode === 'cloud') {
    try {
      const remote = await fetchSettings();
      if (remote) {
        applyValues(remote);
        return;
      }
    } catch {
      // Fall back to local cache when the backend is unreachable.
    }
  }
  applyValues(loadFromLocalStorage());
}

export function isReticleEnabled(): boolean {
  return values.reticle;
}

export function setReticleEnabled(v: boolean): void {
  values.reticle = v;
  persistLocal();
  persistCloud();
}

export function isInrangeEnabled(): boolean {
  return values.inrange;
}

export function setInrangeEnabled(v: boolean): void {
  values.inrange = v;
  persistLocal();
  persistCloud();
}

export function isAmbientMusicEnabled(): boolean {
  return values.bgm_ambient;
}

export function setAmbientMusicEnabled(v: boolean): void {
  values.bgm_ambient = v;
  persistLocal();
  persistCloud();
  onMusicSettingChanged();
}

export function isBattleMusicEnabled(): boolean {
  return values.bgm_battle;
}

export function setBattleMusicEnabled(v: boolean): void {
  values.bgm_battle = v;
  persistLocal();
  persistCloud();
  onMusicSettingChanged();
}

const OPTIONS: { label: string; get: () => boolean; set: (v: boolean) => void }[] = [
  { label: 'Reticle', get: isReticleEnabled, set: setReticleEnabled },
  { label: 'Inrange', get: isInrangeEnabled, set: setInrangeEnabled },
  { label: 'Background music', get: isAmbientMusicEnabled, set: setAmbientMusicEnabled },
  { label: 'Battle music', get: isBattleMusicEnabled, set: setBattleMusicEnabled },
];

export async function ShowSettings(): Promise<void> {
  await showSettingsDom();
}

export function showSettingsDom(): Promise<void> {
  ensureTitleScreenStyles();
  return new Promise((resolve) => {
    const shell = getTitleShell();
    const inGame = !shell || shell.style.display === 'none';

    const modal = document.createElement('div');
    modal.className = 'merit-settings-modal';
    if (inGame) modal.id = 'merit-settings-overlay';

    const panel = document.createElement('div');
    panel.className = 'merit-title-panel merit-settings-panel';

    const heading = document.createElement('div');
    heading.className = 'merit-title-heading';
    heading.textContent = 'Settings';
    panel.appendChild(heading);

    const toggleRows: HTMLButtonElement[] = [];

    const refreshToggle = (btn: HTMLButtonElement, on: boolean) => {
      btn.textContent = on ? 'On' : 'Off';
      btn.classList.toggle('on', on);
    };

    for (const opt of OPTIONS) {
      const row = document.createElement('div');
      row.className = 'merit-settings-row';

      const label = document.createElement('span');
      label.textContent = opt.label;
      row.appendChild(label);

      const toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'merit-settings-toggle';
      refreshToggle(toggle, opt.get());
      toggle.addEventListener('click', () => {
        opt.set(!opt.get());
        refreshToggle(toggle, opt.get());
      });
      toggleRows.push(toggle);
      row.appendChild(toggle);
      panel.appendChild(row);
    }

    const back = document.createElement('button');
    back.type = 'button';
    back.className = 'merit-title-btn secondary';
    back.textContent = 'Back';
    back.style.marginTop = '8px';
    panel.appendChild(back);

    modal.appendChild(panel);
    (inGame ? document.body : shell!).appendChild(modal);

    const close = () => {
      window.removeEventListener('keydown', onKey);
      modal.remove();
      resolve();
    };

    back.addEventListener('click', close);

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
      }
    };

    window.addEventListener('keydown', onKey);
    back.focus();
  });
}
